import { createElement } from "react";
import { createRoot, type Root } from "react-dom/client";
import { delegate } from "tippy.js";
import { VoteWindowInterface } from "@/appInterface/windows/VoteWindow/interface";
import type { VoteEntity } from "@/appInterface/windows/VoteWindow/VoteResults/types";
import { canv } from "@/canv/global";
import { isOnScreen } from "@/canv/utils";
import { eventEmitter } from "@/eventEmitter";
import type { Group } from "@/group";
import { createStore } from "@/shared/lib/createStore";
import { isItemWrapperGroup } from "@/utils";
import { StartVoteWindow, type StartVoteWindowVote } from "../StartVoteWindow";
import type { ActiveTab, Vote, WidgetListItem } from "./types";
import { createVoteBuildEvent } from "./utils";
import { VoteButtonTimer } from "./VoteButtonTimer";
import { VoteScreen } from "./VoteScreen";
import style from "./VoteWindow.module.css";
import { VoteWindowLayout } from "./VoteWindowLayout";

export function createDefaultWidgetsList(): WidgetListItem[] {
    return [
        { name: "Стикеры", value: "sticker", selectedCount: 0, candidateCount: 0 },
        { name: "Фигуры", value: "shape", selectedCount: 0, candidateCount: 0 },
        { name: "Изображения", value: "image", selectedCount: 0, candidateCount: 0 },
        { name: "Текст", value: "text", selectedCount: 0, candidateCount: 0 },
        { name: "Карточки", value: "card", selectedCount: 0, candidateCount: 0 },
    ];
}

export class VoteWindow {
    widgetsListStore = createStore<WidgetListItem[]>(createDefaultWidgetsList());
    votesCountStore = createStore(1);
    timeCountStore = createStore(1);
    oneVotePerItemStore = createStore(false);
    startVoteButtonDisabledStore = createStore(true);

    constructor() {
        this.screen = new VoteScreen();
        this.interface = new VoteWindowInterface(this);
    }

    updateTimer() {
        const activeVote = canv.canvasController.activeVote as unknown as Vote | null;
        if (!this.timerRoot || !activeVote) return;
        this.timerRoot.render(
            createElement(VoteButtonTimer, {
                startedAt: activeVote.start,
                duration: activeVote.duration,
                onUnmount: this.resetTimer,
            }),
        );
    }

    showStartVoteWindow(vote: StartVoteWindowVote) {
        if (this.startModal) return;

        const startModalDiv = document.createElement("div");
        startModalDiv.id = "startVoteWindow";

        this.startModal = createRoot(startModalDiv);
        this.startModal.render(
            createElement(StartVoteWindow, {
                onClose: () => {
                    this.startModal?.unmount();
                    this.startModal = null;
                    document.body.removeChild(startModalDiv);
                },
                onAccept: () => {
                    if (canv.canvasController.activeVote) {
                        canv.appInterface.activeVote.open();
                    }
                    this.close(false);
                },
                vote,
            }),
        );

        document.body.appendChild(startModalDiv);
    }

    open() {
        if (this.opened) return;

        const voteWindowWrapper = document.createElement("div");
        voteWindowWrapper.classList.add(style.voteWindowWrapper);

        const bottomBarWrapper = document.querySelector("#bottom-bar-wrapper-react");

        if (!bottomBarWrapper) {
            throw new Error("Cannot find module 'bottomBarWrapper'");
        }

        this.voteWindowWrapper = voteWindowWrapper;
        bottomBarWrapper.append(voteWindowWrapper);

        this.root = createRoot(voteWindowWrapper);
        this.render();

        this.opened = true;

        eventEmitter.dispatch("header.button.active.name", {
            name: "vote",
        });
        eventEmitter.dispatch("vote.start.visibility", {
            isVisible: true,
        });
    }

    render() {
        const VoteWindowLayoutElement = createElement(VoteWindowLayout, {
            onClose: this.close,
            activeTab: this.activeTab,
            onChangeTab: this.changeTab,
            onStart: this.handleStart,
            onMount: this.mount,
            isShowResult: this.isShowResult,
            voteResult: this.voteResult,
            setShowResult: (val: boolean, vote?: VoteEntity) => {
                this.isShowResult = val;
                if (vote) {
                    this.voteResult = vote;
                    this.widgetUIDs = new Set(
                        Object.values(vote.children).map((child) => child.linkToOriginalUID),
                    );
                }
                this.render();
            },
            selectResultItem: this.selectResultItem,
            selectedResultItemUID: this.selectedResultItemUID,
        });

        if (this.root) {
            this.root.render(VoteWindowLayoutElement);
        } else {
            throw new Error("VoteWindow can't find this.root");
        }

        delegate("body", {
            target: ".disabledWidgetCheckbox",
            content: "В выделенной области нет подходящих объектов",
            placement: "bottom",
        });
    }

    selectResultItem = (UID: string) => {
        this.selectedResultItemUID = UID;
        let group: Group | undefined;
        for (const item of canv.canvasController.items) {
            if (isItemWrapperGroup(item)) {
                if (item.children[0]?.UID === UID) {
                    group = item;
                    break;
                }
            }
        }

        if (!group) return;

        if (canv.zoomProcent < 100 || !isOnScreen(group)) {
            canv.zoomToVoteResultItem(group as unknown as Group[]);
        }
        this.render();
    };

    mount = (element: HTMLElement) => {
        this.voteWindowElement = element;
        this.showScreen(element);
    };

    showScreen(element: HTMLElement) {
        if (this.activeTab === "new") {
            this.screen?.create(element);
        } else {
            this.screen?.remove();
        }
    }

    changeTab = (activeTab: ActiveTab) => {
        this.activeTab = activeTab;

        eventEmitter.dispatch("vote.start.activeTab", { tab: activeTab });

        if (this.voteWindowElement) {
            this.showScreen(this.voteWindowElement);
        }
        this.render();
    };

    toggle() {
        if (this.opened) {
            this.close();
        } else {
            this.open();
        }
    }

    close = (shouldToggleSidebarVisibility = true) => {
        if (!this.opened) {
            return;
        }
        this.widgetsListStore.set(createDefaultWidgetsList());
        this.votesCountStore.set(1);
        this.timeCountStore.set(1);
        this.oneVotePerItemStore.set(false);
        this.startVoteButtonDisabledStore.set(true);
        this.widgetUIDs.clear();
        this.root?.unmount();
        this.screen?.remove();
        this.opened = false;
        this.isShowResult = false;
        this.activeTab = "new";

        eventEmitter.dispatch("vote.start.activeTab", {
            tab: "new",
        });

        if (this.voteWindowWrapper?.parentNode) {
            this.voteWindowWrapper.parentNode.removeChild(this.voteWindowWrapper);
            this.voteWindowWrapper = null;
        }

        if (shouldToggleSidebarVisibility) {
            eventEmitter.dispatch("header.button.active.name", {
                name: null,
            });
        }
        eventEmitter.dispatch("vote.start.visibility", {
            isVisible: false,
        });
    };

    handleStart = () => {
        canv.intercom.eventMessage(
            createVoteBuildEvent({
                votesCount: this.votesCountStore.getSnapshot(),
                timeCount: this.timeCountStore.getSnapshot(),
                oneVotePerItem: this.oneVotePerItemStore.getSnapshot(),
            }),
        );
        this.widgetsListStore.set(createDefaultWidgetsList());
        this.votesCountStore.set(1);
        this.timeCountStore.set(1);
        this.oneVotePerItemStore.set(false);
        this.startVoteButtonDisabledStore.set(true);
        this.screen?.remove();
        this.widgetUIDs.clear();
        this.close(false);
    };

    updateWidgetList(selectedGroupUIDs: string[] = [], candidates: Group[] = []) {
        const next = createDefaultWidgetsList();

        for (const candidate of candidates) {
            const constructorName = candidate.onlyChild()?.constructorName;
            const normalizedName = constructorName === "textV2" ? "text" : constructorName;
            const idx = next.findIndex((item) => item.value === normalizedName);
            if (idx > -1) {
                next[idx].candidateCount += 1;
                if (selectedGroupUIDs.includes(candidate.UID)) {
                    next[idx].selectedCount += 1;
                }
            }
        }

        const prev = this.widgetsListStore.getSnapshot();
        const changed =
            prev.length !== next.length ||
            prev.some(
                (prevItem, i) =>
                    prevItem.selectedCount !== next[i].selectedCount ||
                    prevItem.candidateCount !== next[i].candidateCount,
            );

        if (changed) {
            const isStartVoteButtonDisabled =
                Boolean(canv.canvasController.activeVote) ||
                candidates.length < 2 ||
                selectedGroupUIDs.length < 2;

            this.startVoteButtonDisabledStore.set(isStartVoteButtonDisabled);

            this.setWidgetsList(next);
        }
    }

    updatePastVoteList() {
        if (this.activeTab === "past" && !this.isShowResult) {
            this.render();
            this.rerenderTrigger = !this.rerenderTrigger;
        }
    }

    resetTimer = () => {
        eventEmitter.dispatch("vote.timer.visibility", { isVisible: false });

        if (!this.opened) {
            eventEmitter.dispatch("header.button.active.name", { name: null });
        }
    };

    showResultState() {
        this.resetTimer();
        this.activeTab = "past";
        eventEmitter.dispatch("vote.start.activeTab", { tab: "past" });
        if (canv.canvasController.pastVotes.length > 0) {
            this.voteResult =
                canv.canvasController.pastVotes[canv.canvasController.pastVotes.length - 1];
            if (this.voteResult) {
                this.widgetUIDs = new Set(
                    Object.values(this.voteResult).map((child) => child.linkToOriginalUID),
                );
            }
            this.isShowResult = true;
        }
        this.open();
    }

    subscribeWidgetsList = this.widgetsListStore.subscribe;

    getWidgetsListSnapshot = this.widgetsListStore.getSnapshot;

    setWidgetsList(next: WidgetListItem[]) {
        this.widgetsListStore.set(next);
    }

    subscribeVotesCount = this.votesCountStore.subscribe;

    getVotesCountSnapshot = this.votesCountStore.getSnapshot;

    setVotesCount(votes: number) {
        this.votesCountStore.set(votes);
    }

    subscribeTimeCount = this.timeCountStore.subscribe;

    getTimeCountSnapshot = this.timeCountStore.getSnapshot;

    setTimeCount(time: number) {
        this.timeCountStore.set(time);
    }

    subscribeStartVoteButtonDisabled = this.startVoteButtonDisabledStore.subscribe;

    getStartVoteButtonDisabled = this.startVoteButtonDisabledStore.getSnapshot;

    subscribeOneVotePerItem = this.oneVotePerItemStore.subscribe;

    getOneVotePerItem = this.oneVotePerItemStore.getSnapshot;

    setOneVotePerItem(isOneVotePerItem: boolean) {
        this.oneVotePerItemStore.set(isOneVotePerItem);
    }

    root: Root | null = null;
    opened = false;
    activeTab: ActiveTab = "new";
    screen: VoteScreen | null = null;
    infoModal: Root | null = null;
    startModal: Root | null = null;
    timerRoot: Root | null = null;
    isShowResult = false;
    voteResult: VoteEntity | null = null;
    selectedResultItemUID = "";
    widgetUIDs: Set<string> = new Set();

    voteWindowElement: HTMLElement | null = null;
    voteWindowWrapper: HTMLElement | null = null;
    interface: VoteWindowInterface;
    rerenderTrigger = false;
}
